/**
 * Importing necessary components and utilities
 */
import useUser from '@hooks/useUser';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Stack,
  SxProps,
  Theme,
  Typography,
  alpha,
  styled,
  useMediaQuery,
} from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridCsvExportMenuItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  gridClasses,
} from '@mui/x-data-grid';
import {
  BlobProvider,
  Document,
  Image,
  Page,
  Text,
  View,
} from '@react-pdf/renderer';
import {
  selectPropertyDetails,
  selectPropertyTaxInfo,
  selectedModule,
} from '@store/selector/property.selector';
import moment, { Moment } from 'moment';
import {
  CSSProperties,
  FC,
  ReactNode,
  SetStateAction,
  useLayoutEffect,
  useState,
} from 'react';
import { NoDataIcon } from '../../assets/icons/datatable/NoData';
import servoLogo from '../../assets/report/servo-report-logo.png';
import { getColor } from '../../assets/theme';
import { useAppSelector } from '../../store';
import { convertValuesToString, dateFormat } from '../../utils/format';
import { useCurrentModule } from '@hooks/useCurrentModule';

/**
 * Interface for print settings
 */
interface PrintSettingsInterface {
  removeColumn?: string[];
  title?: string;
  orientation?: 'portrait' | 'landscape';
  pagePadding?: string | number;
  header?: {
    font?: {
      size?: number;
      weight?: number;
    };
  };
  body?: {
    font?: {
      size?: number;
      weight?: number;
    };
  };
  fileName?: string;
  paperSize?: 'A4' | 'A5' | 'A6' | 'A3' | 'LETTER' | 'LEGAL';
  selectedDates?: {
    from?: Moment;
    to?: Moment;
  };
}
export interface BaseDataTableOptions {
  tableStyle?: SxProps;
  disableSearch?: boolean;
  sortBy?: string | { field: string; sort: 'asc' | 'desc' }[];
  viewBy?: 'asc' | 'desc';
  disableAdditionalLoading?: boolean;
  enableExport?: boolean;
  pageSize?: number;
  height?:
    | CSSProperties['height']
    | {
        [key in keyof Theme['breakpoints']['values']]?: CSSProperties['height'];
      };
  stringHeight?: string;
  additionalInitialState?: any;
  skeletonStyle?: SxProps;
  wrapperStyle?: SxProps;
  responsiveHeight?: boolean;
  fullHeight?: boolean;
  disableFilterButtons?: boolean;
  replaceSearch?: ReactNode;
  replaceSort?: ReactNode;
  printSettings?: PrintSettingsInterface;
  customPrintFunction?: () => void;
  notStriped?: boolean;
  disabled?: boolean;
  header?: number;
  hidePagination?: boolean;
}

/**
 * Interface for DataTable component props
 */
export interface DataTableInterface
  extends DataGridProps, BaseDataTableOptions {}

/**
 * Interface for CustomToolbar component props
 */
interface CustomToolbarInterface {
  showQuickFilter?: boolean;
  disableFilterButtons?: boolean;
  disableColumnFilter?: boolean;
  disableColumnSelector?: boolean;
  enableExport?: boolean;
  replaceSearch?: ReactNode;
  replaceSort?: ReactNode;
  setOpenPrint?: React.Dispatch<SetStateAction<boolean>>;
  hasValue?: boolean;
  customPrintFunction?: () => void;
  fileName?: string;
}

/**
 * Opacity constant for odd rows
 */
const ODD_OPACITY = 0.05;

/**
 * Styled DataGrid component with striped rows
 */
const StripedDataGrid = styled(DataGrid, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean; notStriped: boolean }>(
  ({ theme, darkMode, notStriped }) => ({
    ...(notStriped
      ? {}
      : {
          // Styling for even rows
          [`& .${gridClasses.row}.even`]: {
            backgroundColor: darkMode ? '#212121' : theme.palette.grey[100],
            '&:hover, &.Mui-hovered': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '@media (hover: none)': {
                backgroundColor: 'transparent',
              },
            },
            '&.Mui-selected': {
              backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity,
              ),
              '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                  backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY + theme.palette.action.selectedOpacity,
                  ),
                },
              },
            },
          },
          // Styling for odd rows
          [`& .${gridClasses.row}.odd`]: {
            '&:hover, &.Mui-hovered': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
            '&.Mui-selected': {
              '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  0.1 +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
              },
            },
          },
        }),
  }),
);

/**
 * Default styles for the DataTable component.
 * This object contains various styling rules for different parts of the DataTable,
 * including the grid, toolbar, pagination, and individual cells.
 */

const datatableDefaultStyle: SxProps | CSSProperties = {
  height: '100%',
  border: '0!important',
  padding: '24px 24px 0 24px',
  borderRadius: '4px',
  '.MuiDataGrid-iconButtonContainer button': {
    visibility: 'visible',
    background: 'transparent',
    border: 0,
  },
  '.MuiDataGrid-iconButtonContainer': { width: 'auto!important' },
  '.MuiDataGrid-sortIcon': {
    transform: 'rotate(180deg)',
  },
  '& .MuiDataGrid-columnSeparator': {
    visibility: 'visible!important',
    width: 'auto!important',
  },
  '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within, &.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within':
    {
      outline: 'none !important',
    },
  // TOOLBAR
  '.MuiDataGrid-toolbarContainer': { pb: '8px' },
  // PAGINATION
  '.MuiDataGrid-selectedRowCount': {
    whiteSpace: 'nowrap',
  },
  '.MuiTablePagination-actions': { display: 'flex', gap: '8px' },
  '.MuiTablePagination-actions button': {
    bgcolor: 'transparent',
    border: 0,
  },
  '.MuiDataGrid-footerContainer:first-of-type': {
    display: 'none!important',
  },
  '.MuiTablePagination-spacer': {
    display: 'none',
  },
  '.MuiTablePagination-root': {
    width: '100%',
  },
  '.MuiTablePagination-displayedRows': {
    flex: 1,
    textAlign: 'right',
  },
  '.MuiToolbar-root': {
    padding: '0!important',
  },
  // TOOLBAR
  '.MuiDataGrid-toolbarContainer button': {
    padding: '6px 8px',
  },
  '.MuiDataGrid-toolbarContainer .MuiInputBase-root': {
    border: '1px solid #EEE',
    borderRadius: '6px',
    padding: '2px 12px',
    height: '38.7px',
  },
  '.MuiDataGrid-toolbarContainer .MuiInputBase-root::before': {
    border: 0,
  },
  '.MuiDataGrid-toolbarContainer .MuiInputBase-root button': {
    border: 0,
    bgcolor: 'transparent',
  },
  '.MuiFormControl-root': {
    width: { xs: '100%', sm: 'auto' },
  },

  // EDITABLE
  '.editable': {
    position: 'relative',
    cursor: 'pointer',
  },
  '.editable::before': {
    border: '0.5px solid #0000003a',
    borderRadius: '4px',
    p: '10px',
    content: '" "',
    position: 'absolute',
    top: '50%',
    left: '50%',
    height: '80%',
    width: '98%',
    px: '10px',
    transform: 'translate(-50%, -50%)',
  },
  '& .Mui-error': {
    backgroundColor: '#ff56309c',
    border: '1px solid #FF5630',
    borderRadius: '4px',
  },
  '& .editable .MuiInputBase-root': { height: '80%' },
  '[data-testid="LoadIcon"]': { display: 'none' },
  '& .MuiDataGrid-cell--editing , & .MuiDataGrid-row--editing': {
    bgcolor: 'rgb(255 170 0 / 10%)!important',
  },
  '& .flex-cell .MuiFormControl-root ': {
    mx: '2px!important',
  },
  '& .editable .MuiOutlinedInput-notchedOutline': { width: '99%' },
  // HIDE CHECKBOX
  '.disable-checkbox .MuiDataGrid-checkboxInput': {
    display: 'none',
  },

  // CELL
  '.MuiButton-startIcon': {
    mr: 0,
  },
  ".MuiButtonBase-root[aria-label='Export'] .MuiButton-startIcon": {
    fontSize: 'inherit',
  },
  ".MuiButtonBase-root[aria-label='Show filters'] , .MuiButtonBase-root[aria-label='Export'] , .MuiButtonBase-root[aria-label='Select columns']":
    {
      fontSize: 0,
      minWidth: '25px',
      px: 0,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      ':hover': {
        bgcolor: 'transparent',
      },
    },
  ".MuiButtonBase-root[aria-label='Show filters'] .MuiButton-startIcon , .MuiButtonBase-root[aria-label='Export'] .MuiButton-startIcon  , .MuiButtonBase-root[aria-label='Select columns'] .MuiButton-startIcon":
    {
      fontSize: 'inherit',
    },

  // HEADER
  '.no-padding': {
    p: 0,
  },

  // '& .MuiDataGrid-columnSeparator': { visibility: 'visible' },

  // EDITED & NEW & DELETED ROW
  // '.edited': {
  //   backgroundColor: 'rgb(43 52 147 / 10%)',
  // },
  '.new, .edited': {
    backgroundColor: 'rgba(34, 197, 94, 0.05)!important',
  },
  '.deleted': {
    backgroundColor: 'rgba(255, 170, 170, 0.3)!important',
  },

  // ROW
  '.not-selectable': {
    opacity: 0.5,
  },
};

/**
 * Component to display when there are no rows in the DataTable.
 * @returns {JSX.Element} A Stack component with a NoDataIcon and a Typography component.
 */
export const NoRowsOverlay = ({ details }: { details?: string }) => {
  const { darkMode } = getColor();
  return (
    <Stack
      id='no-overlay-ui'
      height='100%'
      alignItems='center'
      justifyContent='center'
    >
      <NoDataIcon sx={{ fontSize: '85px', pb: '8px' }} />
      <Typography
        sx={{
          fontFamily: 'Poppins',
          color: darkMode ? '#ffffffb2' : 'rgba(33, 43, 54, 0.38)',
        }}
      >
        {details || 'No Records Found'}
      </Typography>
    </Stack>
  );
};

/**
 * Component to display when there are no matching results in the DataTable.
 * @returns {JSX.Element} A Stack component with a NoDataIcon and a Typography component.
 */
const NoResultsOverlay = () => {
  const { darkMode } = getColor();
  return (
    <Stack
      sx={{
        '@media print': {
          display: 'none',
        },
      }}
      height='100%'
      alignItems='center'
      justifyContent='center'
    >
      <NoDataIcon sx={{ fontSize: '85px', pb: '8px' }} />
      <Typography
        sx={{
          fontFamily: 'Poppins',
          color: darkMode ? '#ffffffb2' : 'rgba(33, 43, 54, 0.38)',
        }}
      >
        No Matching Record
      </Typography>
    </Stack>
  );
};

/**
 * CustomToolbar component for the DataTable.
 * This component renders a toolbar with various options for filtering, column selection, and exporting.
 *
 * @param {Object} props - The properties passed to the component
 * @param {boolean} props.disableFilterButtons - If true, filter buttons are disabled
 * @param {boolean} props.disableColumnFilter - If true, column filter button is disabled
 * @param {boolean} props.disableColumnSelector - If true, column selector button is disabled
 * @param {boolean} props.enableExport - If true, export options are enabled
 * @param {boolean} props.showQuickFilter - If true, quick filter is shown
 * @param {Function} props.setOpenPrint - Function to set the print modal open state
 * @param {boolean} props.hasValue - Indicates if the table has any data
 * @param {ReactNode} props.replaceSearch - Custom search component to replace the default one
 * @param {Function} props.customPrintFunction - Custom function for print functionality
 * @returns {JSX.Element} The CustomToolbar component
 * @param {string} props.fileName - If true add the filename when downloading the csv file
 */
export const CustomToolbar: FC<CustomToolbarInterface> = ({
  disableFilterButtons,
  disableColumnFilter,
  disableColumnSelector,
  enableExport,
  showQuickFilter,
  setOpenPrint,
  hasValue,
  replaceSearch,
  replaceSort,
  customPrintFunction,
  fileName,
  ...restProps
}) => {
  return (
    <Stack
      flexDirection='row'
      justifyContent='space-between'
      alignItems='center'
      width='100%'
      gap={2}
    >
      {!!replaceSort ? (
        replaceSort
      ) : (
        <Box>
          {!disableFilterButtons && hasValue && (
            <GridToolbarContainer {...restProps}>
              {!disableColumnSelector && <GridToolbarColumnsButton />}
              {!disableColumnFilter && <GridToolbarFilterButton />}{' '}
              {enableExport && (
                <GridToolbarExportContainer>
                  <GridCsvExportMenuItem options={{ fileName }} />
                  <MenuItem
                    onClick={
                      !!customPrintFunction
                        ? customPrintFunction
                        : () => {
                            setOpenPrint?.(true);
                          }
                    }
                  >
                    Print
                  </MenuItem>
                </GridToolbarExportContainer>
              )}
            </GridToolbarContainer>
          )}
        </Box>
      )}
      {showQuickFilter && !replaceSearch ? (
        <GridToolbarQuickFilter
          variant='outlined'
          size='small'
          sx={{ pb: 0 }}
        />
      ) : showQuickFilter && replaceSearch ? (
        replaceSearch
      ) : (
        <></>
      )}
    </Stack>
  );
};

/**
 * PrintTable component for rendering a printable version of the table.
 * This component uses react-pdf to create a PDF document of the table data.
 *
 * @param {Object} props - The properties passed to the component
 * @param {any[]} props.rows - The data rows to be displayed in the table
 * @param {GridColDef[]} props.columns - The column definitions for the table
 * @param {PrintSettingsInterface} [props.printSettings] - Optional settings for customizing the print output
 * @returns {JSX.Element} The PrintTable component
 */
const PrintTable = ({
  rows,
  columns,
  printSettings,
  propertyDetails,
  propertyInfo,
  userData,
  currentModuleName,
  title,
}: {
  rows: any[];
  columns: GridColDef[];
  printSettings?: PrintSettingsInterface;
  header?: number;
  propertyDetails: { propertyName: string; propertyAddress: string };
  propertyInfo: string;
  userData: { name: string };
  currentModuleName?: string;
  title?: string;
}) => {
  const headerFontSize = printSettings?.header?.font?.size || 8;
  return (
    <Document>
      <Page
        size={printSettings?.paperSize ?? 'A4'}
        style={{ padding: '.75cm', fontFamily: 'Roboto' }}
        orientation={printSettings?.orientation || 'landscape'}
      >
        <View fixed>
          <View fixed>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 20,
              }}
            >
              {/* Put Dynamic logo here */}
              {<Image src={servoLogo} style={{ height: 35, marginRight: 8 }} />}
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  flex: 1,
                  paddingBottom: 10,
                }}
              >
                <View>
                  <Text
                    style={{
                      fontSize: printSettings?.header?.font?.size || 9,
                    }}
                  >
                    {propertyDetails.propertyName}
                  </Text>
                  <Text
                    style={{
                      fontSize: headerFontSize,
                    }}
                  >
                    {propertyDetails.propertyAddress}
                  </Text>
                  {printSettings?.selectedDates?.from &&
                    printSettings?.selectedDates?.to && (
                      <Text style={{ fontSize: headerFontSize }}>
                        {moment(printSettings.selectedDates.to).diff(
                          moment(printSettings.selectedDates.from),
                          'days',
                        ) === 0
                          ? `On ${printSettings.selectedDates.to.format(dateFormat)}`
                          : `From ${printSettings.selectedDates.from.format(dateFormat)} to ${printSettings.selectedDates.to.format(dateFormat)}`}
                      </Text>
                    )}
                </View>

                <View
                  style={{
                    textAlign: 'left',
                  }}
                >
                  <Text
                    style={{
                      fontSize: headerFontSize,
                    }}
                  >
                    {propertyInfo && (
                      <Text
                        style={{
                          fontSize: headerFontSize,
                        }}
                      >
                        TIN: {propertyInfo}
                      </Text>
                    )}
                  </Text>
                  <Text
                    style={{
                      fontSize: headerFontSize,
                    }}
                  >
                    {currentModuleName} Cloud
                  </Text>
                  <Text
                    style={{
                      fontSize: headerFontSize,
                    }}
                  >
                    {userData?.name}
                  </Text>
                  <Text
                    style={{
                      fontSize: headerFontSize,
                    }}
                  >
                    Print Date/Time:{' '}
                    {moment().format(`${dateFormat} hh:mm:ss A`)}
                  </Text>
                  <Text
                    style={{
                      fontSize: headerFontSize,
                    }}
                    render={({ pageNumber, totalPages }) =>
                      `Page ${pageNumber} of ${totalPages}`
                    }
                  ></Text>
                </View>
              </View>
            </View>

            <Text
              style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold' }}
            >
              {title}
            </Text>
          </View>
          <View
            style={{
              width: '100%',
              borderBottomWidth: 1,
              borderBottomColor: '#000',
              borderBottomStyle: 'solid',
              marginTop: 6,
              marginBottom: 6,
            }}
          />
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            {columns.map((column: GridColDef) => {
              return (
                <Text
                  style={{
                    fontSize: headerFontSize,
                    width: column.width,
                    maxWidth: column.maxWidth,
                    minWidth: column.minWidth,
                    flex: column.flex,
                    textAlign: column.align,
                    fontWeight: 600,
                    padding: 6,
                  }}
                >
                  {column.headerName || ''}
                </Text>
              );
            })}
          </View>
          <View
            style={{
              width: '100%',
              borderBottomWidth: 1,
              borderBottomColor: '#000',
              borderBottomStyle: 'solid',
              marginTop: 6,
              marginBottom: 6,
            }}
          />
        </View>
        <View>
          {rows.map((row: any, i: number) => {
            return (
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  backgroundColor: i % 2 ? 'rgba(0,0,0,.125)' : '#ffffff',
                }}
              >
                {columns.map((column) => {
                  return (
                    <Text
                      style={{
                        fontSize: printSettings?.body?.font?.size || 8,
                        width: column.width,
                        maxWidth: column.maxWidth,
                        minWidth: column.minWidth,
                        flex: column.flex,
                        textAlign: column.align,
                        padding: 6,
                      }}
                    >
                      {row[column.field] || ''}
                    </Text>
                  );
                })}
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

const omit = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> =>
  Object.fromEntries(
    Object.entries(obj).filter(([k]) => !keys.includes(k as K)),
  ) as Omit<T, K>;

/**
 * DataTable component for displaying data in a tabular format with various customization options.
 *
 * @component
 * @param {Object} props - The properties that define the DataTable behavior and appearance.
 * @param {Array} props.rows - The data to be displayed in the table.
 * @param {Object} [props.tableStyle={}] - Custom styles for the table.
 * @param {Array} props.columns - The column definitions for the table.
 * @param {boolean} [props.disableSearch=false] - Whether to disable the search functionality.
 * @param {string} [props.sortBy] - The field to sort the data by default.
 * @param {boolean} [props.disableAdditionalLoading=false] - Whether to disable additional loading behavior.
 * @param {number} [props.pageSize] - The number of rows per page.
 * @param {number|string} [props.height] - The height of the table.
 * @param {Object} [props.additionalInitialState={}] - Additional initial state for the table.
 * @param {Object} [props.slotProps={}] - Props for custom slot components.
 * @param {Object} [props.initialState={}] - Initial state for the table.
 * @param {Object} [props.slots={}] - Custom slot components.
 * @param {Object} [props.skeletonStyle={}] - Custom styles for the loading skeleton.
 * @param {Object} [props.wrapperStyle={}] - Custom styles for the table wrapper.
 * @param {'asc'|'desc'} [props.viewBy] - The default sort direction.
 * @param {boolean} [props.responsiveHeight=false] - Whether to use responsive height.
 * @param {ReactNode} [props.replaceSearch] - Custom search component.
 * @param {boolean} [props.disableFilterButtons=false] - Whether to disable filter buttons.
 * @param {boolean} [props.disableColumnFilter] - Whether to disable column filtering.
 * @param {boolean} [props.disableColumnSelector] - Whether to disable column selection.
 * @param {boolean} [props.enableExport] - Whether to enable export functionality.
 * @param {Object} [props.printSettings] - Settings for print functionality.
 * @param {Object} props.etc - Additional props to be passed to the DataGrid component.
 * @returns {JSX.Element} The DataTable component.
 */
const DataTable: FC<DataTableInterface> = ({
  rows,
  tableStyle = {},
  columns,
  disableSearch = false,
  sortBy,
  disableAdditionalLoading = true,
  pageSize,
  height,
  additionalInitialState = {},
  slotProps = {},
  initialState = {},
  slots = {},
  skeletonStyle = {},
  wrapperStyle = {},
  viewBy,
  responsiveHeight = false,
  fullHeight = false,
  replaceSearch,
  replaceSort,
  disableFilterButtons = false,
  disableColumnFilter,
  disableColumnSelector,
  enableExport,
  printSettings,
  notStriped = false,
  pageSizeOptions,
  header,
  hidePagination,
  ...etc
}) => {
  const [pageSizeOpt, setPageSizeOpt] = useState([
    10, 25, 50, 100,
    // { value: -1, label: 'All' },
  ]);

  const [openPrint, setOpenPrint] = useState(false);
  const currentModule = useCurrentModule();
  const currentModuleName = useAppSelector((state) =>
    selectedModule(state, currentModule),
  );
  const isSmallScreen = useMediaQuery('(max-width:1600px)');

  const darkMode = useAppSelector(
    (state) => state.configuration.settings.darkMode,
  );

  const propertyDetails = useAppSelector(selectPropertyDetails);
  const propertyInfo = useAppSelector(selectPropertyTaxInfo);
  const userData = useUser();

  /**
   * Effect to handle initial loading and page size setup.
   */
  useLayoutEffect(() => {
    setPageSizeOpt((prev) => {
      if (Array.isArray(pageSizeOptions)) {
        return pageSizeOptions;
      }

      if (pageSize) {
        const objects = prev.filter((item) => typeof item !== 'number');

        const numericValues = prev.filter(
          (item): item is number => typeof item === 'number',
        );

        // Add pageSize if not present
        const allNumbers = numericValues.includes(pageSize)
          ? numericValues
          : [pageSize, ...numericValues];

        // Remove duplicates
        const uniqueNumbers = Array.from(new Set(allNumbers));

        return [...uniqueNumbers, ...objects];
      }

      return prev;
    });
  }, [pageSize, pageSizeOptions]);

  const wrapperCombinedStyles: SxProps = {
    height: fullHeight
      ? '100%!important'
      : responsiveHeight
        ? {
            xs: 'calc(100% - 150px)!important',
            md: 'calc(100% - 90px)!important',
            xl: 'calc(100% - 85px)!important',
          }
        : isSmallScreen
          ? height || 499.4
          : height || 680,
    width: '100%',
    '.datatable *::-webkit-scrollbar-thumb': {},
    '.datatable *::-webkit-scrollbar': { width: '9px', height: '9px' },
    ...wrapperStyle,
  };

  const datagridCombinedStyles: SxProps = {
    '& .MuiDataGrid-virtualScroller': {
      overflow:
        etc?.loading || (rows?.length <= 0 && !etc?.loading)
          ? 'hidden'
          : 'auto',
    },
    pointerEvents: etc?.loading || etc?.disabled ? 'none' : 'auto',
    opacity: etc?.disabled ? 0.5 : 1,
    bgcolor: getColor()?.white || '#FFF',
    ...datatableDefaultStyle,
    ...tableStyle,
    '.MuiDataGrid-toolbarContainer': {
      flexWrap: slots?.toolbar ? 'wrap!important' : 'nowrap!important',
    },
    '.MuiDataGrid-footerContainer': {
      display: hidePagination ? 'none!important' : undefined,
    },
  };

  const mergedSx: SxProps<Theme> = {
    ...datagridCombinedStyles,
    ...((etc?.sx ?? {}) as any),
  };

  return (
    <>
      <Box className='datatable-wrapper' sx={wrapperCombinedStyles}>
        <StripedDataGrid
          notStriped={notStriped}
          darkMode={darkMode}
          className='datatable'
          rows={rows}
          columns={columns?.map(
            (item): GridColDef => ({
              ...item,
              // getApplyQuickFilterFn: (value) => {
              //   if (!value) return null;

              //   const filterValue = value.toLowerCase().trim();
              //   const wordRegex = new RegExp(`\\b${filterValue}\\b`, 'i');

              //   return (cellValue) => {
              //     const actualValue =
              //       typeof cellValue === 'object' && cellValue !== null
              //         ? cellValue.value
              //         : cellValue;

              //     return wordRegex.test(String(actualValue ?? ''));
              //   };
              // },
            }),
          )}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          initialState={{
            ...initialState,
            pagination: {
              ...initialState?.pagination,
              // paginationModel: {
              //   pageSize:
              //     pageSize !== undefined &&
              //     pageSizeOpt.some((opt) => opt === pageSize)
              //       ? pageSize
              //       : pageSizeOpt[0],
              // },
            },
            sorting: {
              ...initialState?.sorting,
              sortModel:
                typeof sortBy === 'string'
                  ? [{ field: sortBy, sort: viewBy ? viewBy : 'asc' }]
                  : sortBy && Array.isArray(sortBy)
                    ? sortBy
                    : viewBy
                      ? [{ field: '', sort: viewBy }]
                      : [],
            },
          }}
          slots={{
            toolbar: slots?.toolbar ?? CustomToolbar,
            noRowsOverlay: NoRowsOverlay,
            noResultsOverlay: NoResultsOverlay,
            ...slots,
          }}
          slotProps={{
            ...slotProps,
            toolbar: {
              ...slotProps.toolbar,
              showQuickFilter: !disableSearch,
              replaceSearch,
              replaceSort,
              disableFilterButtons,
              disableColumnFilter,
              disableColumnSelector,
              enableExport,
              setOpenPrint,
              hasValue: rows.length > 0,
            },
            columnsPanel: {
              sx: {
                '.MuiDataGrid-panelContent': {
                  maxHeight: {
                    xs: 'auto',
                    sm: '100px',
                    md: '200px',
                    xl: 'auto',
                  },
                },
              },
            },
          }}
          rowHeight={45}
          checkboxSelection
          disableDensitySelector
          disableColumnMenu
          disableVirtualization
          paginationMode='client'
          sortingMode='client'
          filterMode='client'
          keepNonExistentRowsSelected={etc.paginationMode == 'server'}
          {...omit(etc, ['sx'])}
          // autoPageSize add false value on this to show table scrollbar
          // autoPageSize={
          //   autoPageSize && rows.length >= 101 ? false : (autoPageSize ?? true)
          // }
          // pageSizeOptions={
          //   pageSize && pageSize >= 101 ? [pageSize] : pageSizeOpt
          // }
          pageSizeOptions={etc?.autoPageSize ? [] : pageSizeOpt}
          sx={mergedSx}
          loading={etc?.loading}
        />
      </Box>
      <Modal open={openPrint} onClose={() => setOpenPrint(false)}>
        <Box
          sx={{
            height: '100vh',
            width: '100vw',
            left: 0,
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper style={{ width: '75vw', height: 'auto' }}>
            <Stack gap={1}>
              <Stack
                direction='row'
                gap={{ xs: 1, md: 2 }}
                sx={{
                  p: '22px',
                  borderBottom: '1px solid #EEE',
                  position: 'sticky',
                  top: 0,
                  zIndex: 21,
                  pb: '18px',
                  justifyContent: 'space-between',
                  bgcolor: getColor().white,
                  borderRadius: '4px',
                }}
              >
                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                  PDF Viewer
                </Typography>
                <IconButton onClick={() => setOpenPrint(false)}>
                  <Close />
                </IconButton>
              </Stack>
              {openPrint && !!rows.length && !!columns.length && (
                <BlobProvider
                  document={
                    <PrintTable
                      rows={rows.map(convertValuesToString)}
                      columns={columns.filter(
                        ({ field }) =>
                          !printSettings?.removeColumn?.includes(field),
                      )}
                      printSettings={printSettings}
                      propertyDetails={propertyDetails}
                      propertyInfo={propertyInfo.property?.tin ?? ''}
                      userData={userData}
                      header={header}
                      currentModuleName={currentModuleName}
                      title={printSettings?.title || printSettings?.fileName}
                    />
                  }
                >
                  {({ url, loading }) =>
                    loading ? (
                      <span>Preparing PDF...</span>
                    ) : (
                      <>
                        <Stack alignItems='center'>
                          <Button
                            variant='contained'
                            component='a'
                            href={url ?? '#'}
                            download={
                              (printSettings?.fileName ?? 'report') + '.pdf'
                            }
                          >
                            Download PDF
                          </Button>
                        </Stack>
                        <iframe
                          src={url ? `${url}#toolbar=0` : ''}
                          style={{
                            width: '100%',
                            height: '82vh',
                            border: 'none',
                          }}
                        />
                      </>
                    )
                  }
                </BlobProvider>
              )}
            </Stack>
          </Paper>
        </Box>
      </Modal>
    </>
  );
};

export default DataTable;
